import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { createRoute, deleteRoute, updateRoute } from "../../api/routes";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";

const RouteTab = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [newRoute, setNewRoute] = useState({ routename: "", aid: "", routeno: "", map: "" });
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    fetchRoutes();
    fetchAreas();
  }, []);

  const fetchRoutes = async () => {
    try {
      const data = await customFetch("routes");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await customFetch("areas");
      if (data.status && Array.isArray(data.data)) {
        setAreas(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewRoute({ routename: "", aid: "", routeno: "" });
  };

  const handleSubmitNewRoute = async () => {
    if (newRoute.routename && newRoute.aid && newRoute.routeno) {
      try {
        const response = await createRoute(newRoute);
        if (response.status && response.data) {
          const newRouteData = Array.isArray(response.data) ? response.data[0] : response.data;
          const area = areas.find((a) => a.aid === newRouteData.aid);
          const formattedNewRoute = {
            ...newRouteData,
            Area: {
              aid: newRouteData.aid,
              code: area?.code,
              Emirate: { name: area?.emirateName },
            },
          };
          setRows((prevRows) => [...prevRows, formattedNewRoute]);
          handleCloseModal();
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error creating route:", error);
      }
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setSelectedRowId(id);
    setOpenConfirmDialog(true);
  };

  const handleDialogClose = (confirmed) => {
    setOpenConfirmDialog(false);
    if (confirmed) {
      handleConfirmDelete();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteArea(selectedRowId);
      setRows(rows.filter((row) => row.rid !== selectedRowId));
    } catch (error) {
      console.error("Error deleting area:", error);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    updateRoute(newRow.rid, updatedRow).then(() => {
      setRows(rows.map((row) => (row.rid === newRow.rid ? updatedRow : row)));
    });
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    { field: "routename", headerName: "Route Name", width: 200, editable: true },
    { field: "routeno", headerName: "Route No", width: 120, editable: true },
    {
      field: "areaCode",
      headerName: "Area Code",
      width: 120,
      valueGetter: (params) => params.row.Area?.code,
    },
    {
      field: "emirateName",
      headerName: "Emirate",
      width: 150,
      valueGetter: (params) => params.row.Area?.Emirate?.name,
    },
    { field: "map", headerName: "MAP Url", flex: 1, minwidth: 150, editable: true },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 20, padding: "0 0px" }}>
                  Save
                </Button>
              }
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 40, padding: "0 0px" }}>
                  Cancel
                </Button>
              }
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={handleOpenModal}>
          Add New Route
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.rid}
      />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-route-modal"
        aria-describedby="modal-to-add-new-route"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 id="add-route-modal">Add New Route</h2>
          <TextField
            autoFocus
            margin="dense"
            id="routename"
            label="Route Name"
            type="text"
            fullWidth
            variant="standard"
            value={newRoute.routename}
            onChange={(e) => setNewRoute({ ...newRoute, routename: e.target.value })}
          />
          <TextField
            margin="dense"
            id="routeno"
            label="Route No"
            type="text"
            fullWidth
            variant="standard"
            value={newRoute.routeno}
            onChange={(e) => setNewRoute({ ...newRoute, routeno: e.target.value })}
          />
          <TextField
            margin="dense"
            id="map"
            label="Map URL"
            type="text"
            fullWidth
            variant="standard"
            value={newRoute.map}
            onChange={(e) => setNewRoute({ ...newRoute, map: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="area-select-label">Area</InputLabel>
            <Select
              labelId="area-select-label"
              id="area-select"
              value={newRoute.aid}
              label="Area"
              onChange={(e) => setNewRoute({ ...newRoute, aid: e.target.value })}
            >
              {areas.map((area) => (
                <MenuItem key={area.aid} value={area.aid}>
                  {area.code} - {area.emirateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitNewRoute} variant="contained">
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this Route? This action cannot be undone."
        onClose={handleDialogClose}
        onConfirm={() => handleDialogClose(true)}
      />
    </Box>
  );
};

export default RouteTab;
